import React, { useState } from 'react'
import { Box, Select, TextArea, Button } from '@deal/components'
import styles from './styles.css'
import escalationTypes from '../../Orders/escalationTypes'
import escalationStatus from '../../Orders/escalationStatus'
import {
  OrderListLineItemFragment,
  MyOrderListItemFragment,
  OrderEscalationType,
  EscalateOrderComponent,
  MyOrdersConnectionDocument,
  StartSupportConversationComponent
} from '#src/generated/graphql'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import Redirect from '#src/components/Redirect'
import ChatIcon from './chat.svg'

interface OrderEscalationFormProps {
  userId: string
  orders: [MyOrderListItemFragment]
  selectedOrder?: MyOrderListItemFragment
  selectedLineItem?: OrderListLineItemFragment
  onRequestClosed?: () => void
  createNext: boolean
}

const OrderEscalationForm: React.SFC<OrderEscalationFormProps> = ({
  orders,
  userId,
  selectedOrder,
  selectedLineItem,
  onRequestClosed,
  createNext
}) => {
  const orderEscalationTypeOptions = [
    {
      value: OrderEscalationType.ITEM_UNAVAILABLE,
      label: escalationTypes[OrderEscalationType.ITEM_UNAVAILABLE]
    },
    {
      value: OrderEscalationType.MISSING_SPECS,
      label: escalationTypes[OrderEscalationType.MISSING_SPECS]
    },
    {
      value: OrderEscalationType.ORDER_ADJUSTMENT,
      label: escalationTypes[OrderEscalationType.ORDER_ADJUSTMENT]
    },
    {
      value: OrderEscalationType.CANCELLATION,
      label: escalationTypes[OrderEscalationType.CANCELLATION]
    },
    { value: OrderEscalationType.RETURN, label: escalationTypes[OrderEscalationType.RETURN] },
    { value: OrderEscalationType.EXCHANGE, label: escalationTypes[OrderEscalationType.EXCHANGE] },
    {
      value: OrderEscalationType.MISSING_ITEM,
      label: escalationTypes[OrderEscalationType.MISSING_ITEM]
    },
    {
      value: OrderEscalationType.TRACKING_INQUIRY,
      label: escalationTypes[OrderEscalationType.TRACKING_INQUIRY]
    },
    {
      value: OrderEscalationType.ORDER_INFORMATION,
      label: escalationTypes[OrderEscalationType.ORDER_INFORMATION]
    }
  ]
  const [orderId, setOrderId] = useState(
    selectedOrder ? selectedOrder.id : orders && orders.length > 0 ? orders[0].id : ''
  )
  const [lineItemId, setLineItemId] = useState(selectedLineItem ? selectedLineItem.id : '')
  const [type, setType] = useState()
  const [reason, setReason] = useState()
  const [createdEscalation, setCreatedEscalation] = useState()
  const [redirectUrl, setRedirectUrl] = useState('')
  return (
    <EscalateOrderComponent
      refetchQueries={[
        {
          query: MyOrdersConnectionDocument,
          variables: { first: 100, query: { userId: userId } }
        }
      ]}
      onCompleted={response => {
        toast.success('Item escalation created.')
        if (createNext && response.createOrderEscalation.orderEscalation) {
          setCreatedEscalation(response.createOrderEscalation.orderEscalation)
        } else if (onRequestClosed) {
          onRequestClosed()
        }
      }}
      onError={() => toast.error('Unable to create escalation')}
    >
      {escalateOrder => {
        if (redirectUrl) return <Redirect to={redirectUrl} />
        if (createdEscalation) {
          return (
            <>
              <h2>Support ticket created</h2>
              <p>
                You can track the status of your request in <a href="/account/orders">My orders</a>
              </p>
              <div className={styles.escalationContainer}>
                <div className={styles.escalationTitleContainer}>
                  <span className={styles.escalationTitle}>
                    Support ticket created {format(createdEscalation.createdAt, 'MMM dd, YYYY')}
                  </span>
                  <span className={styles.escalationTitle}>
                    {createdEscalation.consumerFriendlyId || createdEscalation.id}
                  </span>
                </div>
                <div className={styles.escalationInfoContainer}>
                  <div className={styles.escalationTypeContainer}>
                    <div>
                      <span className={styles.escalationInfotag}>Request: </span>
                      <span className={styles.escalationInfo}>
                        {escalationTypes[createdEscalation.type]}
                      </span>
                    </div>
                    <StartSupportConversationComponent
                      variables={{ input: { consumerId: userId } }}
                      onCompleted={response => {
                        if (
                          response.startSupportConversation &&
                          response.startSupportConversation.supportConversation &&
                          response.startSupportConversation.supportConversation.conversation
                        ) {
                          setRedirectUrl(
                            '/inbox/' +
                              response.startSupportConversation.supportConversation.conversation
                                .id +
                              '?support'
                          )
                        }
                      }}
                      onError={() => toast.error('Unable to start conversation with support')}
                    >
                      {startSupportConversation => (
                        <Button
                          size="small"
                          variant="link"
                          className={styles.viewOrderLink}
                          onClick={() => {
                            startSupportConversation()
                          }}
                        >
                          <span className={styles.desktopOnly}>Chat with support</span>
                          <ChatIcon className={styles.mobileOnly} />
                        </Button>
                      )}
                    </StartSupportConversationComponent>
                  </div>
                  <div>
                    <span className={styles.escalationInfotag}>Status: </span>
                    <span className={styles.escalationInfo}>
                      {escalationStatus[createdEscalation.status]}
                    </span>
                  </div>
                  {createdEscalation.assignedTo && (
                    <div>
                      <span className={styles.escalationInfotag}>Support agent: </span>
                      <span className={styles.escalationInfo}>
                        {createdEscalation.assignedTo.firstName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <Button
                className={styles.button}
                size="small"
                variant="primary"
                onClick={() => setCreatedEscalation(undefined)}
              >
                Create another ticket
              </Button>
            </>
          )
        }
        return (
          <>
            <Box.Item>
              <div className={styles.selectsContainer}>
                <div className={styles.selectContainer}>
                  <Select
                    label="What is your order number?"
                    disabled={!orders || orders.length < 1}
                    onChange={e => setOrderId(e.target.value)}
                    value={orderId}
                    display="standard"
                    options={
                      orders
                        ? orders
                            .map(order => ({
                              label: order.consumerFriendlyId || order.id,
                              value: order.id
                            }))
                            .concat([{ label: 'None', value: '' }])
                        : [{ label: 'None', value: '' }]
                    }
                  />
                </div>
                <div className={styles.selectContainer}>
                  <Select
                    label="What item is this request related to?"
                    disabled={!orders || orders.length < 1 || !orderId}
                    onChange={e => setLineItemId(e.target.value)}
                    value={lineItemId}
                    display="standard"
                    options={
                      !orderId
                        ? [{ label: 'None', value: '' }]
                        : orders
                            .filter(order => order.id === orderId)[0]
                            .lineItems.filter(
                              item => item.source.__typename === 'OrderLineItemSellableSource'
                            )
                            .map(lineItem => ({
                              label: lineItem.title,
                              value: lineItem.id
                            }))
                            .concat([{ label: 'None', value: '' }])
                    }
                  />
                </div>
              </div>
            </Box.Item>
            <Box.Item className={styles.selectsContainer}>
              <Select
                label="Request type"
                onChange={e => setType(e.target.value as OrderEscalationType)}
                value={type}
                display="standard"
                options={[{ label: 'None', value: '' }].concat(orderEscalationTypeOptions)}
              />
            </Box.Item>
            <Box.Item>
              <div className={styles.textContainer}>
                <span className={styles.textLabel}>What can we help you with? </span>
                <TextArea rows={15} value={reason} onChange={e => setReason(e.target.value)} />
              </div>
            </Box.Item>
            <Button
              className={styles.button}
              size="small"
              disabled={!orderId || !type}
              variant="primary"
              onClick={() =>
                escalateOrder({
                  variables: {
                    input: {
                      orderId: orderId,
                      userId: userId,
                      orderLineItemId: lineItemId || undefined,
                      type: type,
                      dueDate: undefined,
                      reason: reason,
                      assignedToId: undefined
                    }
                  }
                })
              }
            >
              Submit
            </Button>
          </>
        )
      }}
    </EscalateOrderComponent>
  )
}

export default OrderEscalationForm
